// Shared display/parse helpers for amount inputs driven by NumericKeypad.
// Used by DepositCardScreen (Top-up) and WithdrawScreen so the typing UX
// matches across the two screens: thousands separated by a regular space,
// decimal mark `,`, fractional part capped at maxFractional chars at display
// time (full precision flows into the request body via parseAmount).

/**
 * Normalise a free-text entry to the canonical display format ("1 234,56").
 * Accepts mixed input ("1234.5", "1,234,5", "1 234.5") and produces a single
 * consistent value the keypad can keep typing into.
 *
 * `maxFractional` ограничивает количество цифр в дробной части. Default 2 —
 * для keypad-typing UX (юзер пишет фиат-like). Для full-precision crypto
 * случаев (Maximum-button → token_decimals) передай 8 или сколько нужно.
 * Bumps over the cap are dropped (not rounded).
 */
export const formatAmount = (raw: string, maxFractional: number = 2): string => {
  // Keep digits + separators only; treat any `.` as `,`.
  const cleaned = raw.replace(/[^0-9.,]/g, '').replace(/\./g, ',');
  const firstComma = cleaned.indexOf(',');
  let intPart: string;
  let fracPart: string;
  if (firstComma === -1) {
    intPart = cleaned;
    fracPart = '';
  } else {
    // Strip stray commas in the integer slice (e.g. "1,234,5" → int="1234").
    intPart = cleaned.slice(0, firstComma).replace(/,/g, '');
    // Drop further separators in the fractional slice, then cap to maxFractional.
    const frac = cleaned.slice(firstComma + 1).replace(/[,.]/g, '').slice(0, maxFractional);
    fracPart = ',' + frac;
  }
  // Remove leading zeros but keep a single "0" when nothing else is left.
  intPart = intPart.replace(/^0+(\d)/, '$1') || '0';
  // Thousands separator: regular space.
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return intFormatted + fracPart;
};

/**
 * Parse the display-formatted input back to a number: strip thousands
 * spaces, replace `,` with `.` so `parseFloat` works. Returns 0 on
 * non-positive or non-finite values so the caller can still use the result
 * directly in arithmetic without an extra guard.
 */
export const parseAmount = (s: string): number => {
  const normalized = s.replace(/\s/g, '').replace(',', '.');
  const n = parseFloat(normalized);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Render a number into the canonical display format. По умолчанию cap'ит на
 * 2 fractional digits (legacy keypad-typing UX); для crypto-precision
 * (Maximum button → token_decimals) передай нужное число.
 *
 * Принимает `number | string`. **Для Maximum-кейса предпочтительно
 * передавать STRING** (например `activeBalance.available_amount` напрямую),
 * чтобы не проходить через `parseFloat` который теряет точность на
 * пограничных случаях вроде `3.06839508` (в IEEE 754 хранится как
 * `3.0683950799999998` → toString даёт `"3.0683950799999998"` → slice
 * до 8 знаков даёт `"3.06839507"` — на 1 меньше!).
 *
 * Truncation (а не округление) сделана через string slice — критично для
 * Maximum: округление вверх для 3.06839508 даёт 3.07 > balance → submit
 * button disabled.
 *
 * @param n             Source-сумма (number или string из backend'а).
 * @param maxFractional Число знаков после запятой. Default 2.
 */
export const numberToAmountDisplay = (n: number | string, maxFractional: number = 2): string => {
  const safeFractional = Math.max(0, Math.min(maxFractional, 20));

  let str: string;
  if (typeof n === 'string') {
    // Trust the backend's decimal string — никаких parseFloat'ов чтобы не
    // потерять точность. Validation: пустая или невалидная → "0".
    str = n.trim();
    if (str === '' || str === '0' || str === '0.' || str === '-0') return '0';
    // Quick sanity: должно быть число-подобное.
    if (!/^[-+]?\d*\.?\d+$/.test(str)) return '0';
    // Отрицательное / ноль → "0".
    if (parseFloat(str) <= 0) return '0';
    // Strip leading "+".
    if (str.startsWith('+')) str = str.slice(1);
  } else {
    if (!Number.isFinite(n) || n <= 0) return '0';
    str = n.toString();
    // Scientific notation (1e-7) → fixed-decimal, trim trailing zeros.
    if (str.includes('e') || str.includes('E')) {
      str = n.toFixed(20).replace(/0+$/, '').replace(/\.$/, '');
    }
  }

  // Truncate digits после dot — slice = floor semantically (no rounding).
  const dot = str.indexOf('.');
  if (dot >= 0) {
    str = str.slice(0, dot + 1 + safeFractional);
    // Если все дробные оказались отрезаны (maxFractional=0) — убрать точку.
    if (str.endsWith('.')) {
      str = str.slice(0, -1);
    }
  }

  return formatAmount(str, safeFractional);
};

/**
 * A market price for the token lists, quoted in USDT.
 *
 * A price of one USDT or more is money and reads as money: two decimals.
 * The catalogue's own precision governs only prices below one, where those
 * digits are the price — DOGE at 0,1 and ADA at 0,2575 would otherwise
 * collapse. Capped at 8 so an 18-decimal token cannot stretch the row, and
 * trailing zeros are dropped, so USDT reads "1", not "1,000000000000000000".
 *
 * Bitcoin came out as "84 179,719…", truncated mid-number by the row, because
 * its catalogue precision is 8 — eight decimals on a five-figure price.
 *
 * Returns null when there is no price to show.
 */
export const formatMarketPrice = (value: number | string, decimals?: number | null): string | null => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) { return null; }
  const catalogue = decimals == null || !Number.isFinite(decimals)
    ? 4 // no catalogue value — the figure the screens used before
    : Math.min(Math.max(Math.trunc(decimals), 0), 8);
  const places = Math.abs(num) >= 1 ? Math.min(catalogue, 2) : catalogue;
  const fixed = places === 0
    ? num.toFixed(0)
    : num.toFixed(places).replace(/0+$/, '').replace(/\.$/, '');
  const [intPart, decPart] = fixed.split('.');
  const intWithSeparators = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
  return decPart ? `${intWithSeparators},${decPart}` : intWithSeparators;
};

/**
 * A balance, a fee or a payout as the screens print it.
 *
 * `token_decimals` is the contract's precision, not a display setting: the
 * catalogue carries 18 for USDT on Ethereum and on Tron, 18 for ETH, 8 for
 * BTC. Handed straight to the formatter, that is what printed a closed
 * deposit as "+ 240 090,0821917808 2 USDT".
 *
 * The catalogue still caps from below — EUR and the 2-decimal tokens keep
 * their two places — but above it the magnitude decides, on the same
 * reasoning `formatMarketPrice` already follows: a five-figure amount is
 * money and reads as money, while a fraction of a bitcoin is all in the
 * decimals.
 *
 *   ≥ 1000  → 2   240 090,08 USDT
 *   1…1000  → 6   1,5 BTC
 *   < 1     → 8   0,00123456 BTC
 *
 * Truncates rather than rounds, like `numberToAmountDisplay` — rounding a
 * balance up puts it above what the user actually holds — and drops the
 * trailing zeros the backend pads its decimals with, keeping two places so
 * money still reads as money: "1.500000000000000000" BTC becomes "1,50",
 * "2000.000000000000000000" USDT "2 000,00", and a token quoted in whole
 * units keeps none.
 *
 * Pass the backend's own decimal string where there is one: going through a
 * float first is the other half of what produced that 11-digit tail.
 */
export const formatTokenAmount = (
  value: number | string,
  tokenDecimals?: number | null,
): string => {
  const catalogue = tokenDecimals == null || !Number.isFinite(Number(tokenDecimals))
    ? 2
    : Math.max(Math.trunc(Number(tokenDecimals)), 0);
  const magnitude = Math.abs(Number(value));
  let cap: number;
  if (!Number.isFinite(magnitude) || magnitude >= 1000) {
    cap = 2;
  } else if (magnitude >= 1) {
    cap = 6;
  } else {
    cap = 8;
  }
  const display = numberToAmountDisplay(value, Math.min(catalogue, cap));
  const comma = display.indexOf(',');
  const intPart = comma >= 0 ? display.slice(0, comma) : display;
  // Ниже двух знаков не опускаемся (но и выше точности токена не поднимаемся):
  // "0" вместо "0,00" на пустом балансе читается как обрыв, а не как ноль.
  const minPlaces = Math.min(catalogue, 2);
  let frac = (comma >= 0 ? display.slice(comma + 1) : '').replace(/0+$/, '');
  while (frac.length < minPlaces) {
    frac += '0';
  }
  return frac ? `${intPart},${frac}` : intPart;
};
